import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { X, Upload, ImageIcon } from "lucide-react";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import React, { useRef, useState } from "react";
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_KB, getFileSizeErrorMessage } from "@/shared/lib/fileUploadConstants";

const COVER_IMAGE_REQUIREMENTS = {
  width: 614,
  height: 768,
  aspectRatio: 4 / 5,
  aspectTolerance: 0.01,
};

interface UnifiedUploadProps {
  id: string;
  title: string;
  preview: string | null;
  filename?: string | null;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  acceptTypes?: string;
  error?: boolean;
  errorMessage?: string;
  variant?: "cover" | "file" | "lut";
  enableDragDrop?: boolean;
  showRemoveButton?: boolean;
  maxSizeMB?: number;
  onError?: (error: string) => void;
  disabled?: boolean;
}

export const UnifiedUpload = ({
  id,
  title,
  preview,
  onFileChange,
  onRemove,
  acceptTypes,
  error = false,
  errorMessage,
  variant = "file",
  enableDragDrop = true,
  showRemoveButton = true,
  maxSizeMB = MAX_FILE_SIZE_KB / 1024, // 500KB por padrão
  onError,
  disabled = false,
}: UnifiedUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [internalError, setInternalError] = useState<string | null>(null);

  const isLutVariant = variant === "lut";
  const isCoverVariant = variant === "cover";
  const displayPreview = localPreview || preview;

  const acceptedTypes = acceptTypes || (isLutVariant ? "image/jpeg,image/jpg" : "image/*");
  const uploadHeight = 230;
  const isDisabled = disabled;
  const canDragDrop = enableDragDrop && !isDisabled;

  // Validação de arquivo
  const validateFile = async (file: File): Promise<boolean> => {
    // Limpa erro anterior
    setInternalError(null);

    // Validação de tamanho (limite global de 500KB)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const errorMsg = getFileSizeErrorMessage();
      setInternalError(errorMsg);
      onError?.(errorMsg);
      return false;
    }

    // Validação LUT (apenas JPG)
    if (isLutVariant) {
      if (!file.type.match(/image\/(jpeg|jpg)/)) {
        const errorMsg = "Apenas arquivos JPG são permitidos para preview da LUT";
        setInternalError(errorMsg);
        onError?.(errorMsg);
        return false;
      }
      return true;
    }

    // Validação Cover (dimensões específicas)
    if (isCoverVariant && file.type.startsWith("image/")) {
      return await validateImageDimensions(file);
    }

    return true;
  };

  // Validação de dimensões da imagem
  const validateImageDimensions = async (file: File): Promise<boolean> => {
    const objectUrl = URL.createObjectURL(file);

    try {
      const dimensions = await loadImageDimensions(objectUrl);
      const ratio = dimensions.width / dimensions.height;
      const { width, height, aspectRatio, aspectTolerance } = COVER_IMAGE_REQUIREMENTS;

      const isValidSize =
        (dimensions.width === width && dimensions.height === height) ||
        Math.abs(ratio - aspectRatio) <= aspectTolerance;

      if (!isValidSize) {
        const errorMsg =
          `A imagem deve ter ${width}×${height}px (proporção 4:5). ` +
          `Atual: ${dimensions.width}×${dimensions.height}px`;
        setInternalError(errorMsg);
        onError?.(errorMsg);
        return false;
      }

      return true;
    } catch {
      const errorMsg = "Erro ao validar dimensões da imagem";
      setInternalError(errorMsg);
      onError?.(errorMsg);
      return false;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };

  // Carrega dimensões da imagem
  const loadImageDimensions = (src: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error("Erro ao carregar imagem"));
      img.src = src;
    });
  };

  // Processa arquivo selecionado
  const processFile = async (file: File): Promise<boolean> => {
    const isValid = await validateFile(file);
    if (!isValid) return false;

    // Limpa erro e gera preview local
    setInternalError(null);
    const previewUrl = URL.createObjectURL(file);
    setLocalPreview(previewUrl);

    return true;
  };

  // Handler de mudança de arquivo
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;
    const file = e.target.files?.[0];
    if (!file) return;

    const success = await processFile(file);
    if (success) {
      onFileChange(e);
    } else {
      e.target.value = "";
    }
  };

  // Handlers de drag and drop
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!canDragDrop) return;
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!canDragDrop) return;
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    if (!canDragDrop) return;
    e.preventDefault();
    setIsDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const success = await processFile(file);
    if (!success) return;

    // Atualiza o input file
    const fileInput = inputRef.current;
    if (fileInput) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInput.files = dataTransfer.files;
      fileInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  };

  // Handler de remoção
  const handleRemove = () => {
    if (isDisabled) return;
    setLocalPreview(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onRemove();
  };

  // Renderiza preview da imagem
  const renderPreview = () => {
    if (!displayPreview) {
      console.log('No preview to display');
      return null;
    }

    console.log('Rendering preview:', displayPreview, 'variant:', variant);

    if (isLutVariant) {
      return (
        <div className="w-full h-[230px] flex items-center justify-center bg-gradient-to-br from-neutral-900 to-neutral-950 p-4">
          <img
            src={displayPreview}
            alt={title}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            draggable={false}
            onLoad={() => console.log('LUT image loaded successfully')}
            onError={(e) => console.error('LUT image failed to load:', displayPreview, e)}
          />
        </div>
      );
    }

    return (
      <div className="w-full h-[230px] overflow-hidden">
        <img
          src={displayPreview}
          alt={title}
          className="w-full h-full object-cover"
          draggable={false}
          onLoad={() => console.log('Cover image loaded successfully')}
          onError={(e) => console.error('Cover image failed to load:', displayPreview, e)}
        />
      </div>
    );
  };

  // Renderiza área de upload vazia
  const renderEmptyState = () => (
    <>
      <input
        id={id}
        ref={inputRef}
        type="file"
        accept={acceptedTypes}
        onChange={handleFileChange}
        disabled={isDisabled}
        className="hidden"
        aria-label={title}
      />

      <Label
        htmlFor={id}
        className={`flex flex-col justify-center items-center w-full select-none px-4 text-white/70 bg-white/[.03] transition-all duration-200 ${isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:text-white hover:bg-white/[.05]"}`}
        style={{ height: `${uploadHeight}px` }}
      >
        <div className="flex flex-col items-center gap-3 pointer-events-none">
          {/* Ícone */}
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center border border-white/10 shadow-lg">
            <Upload size={20} className="opacity-80" />
          </div>

          {/* Texto principal */}
          <div className="text-center">
            <p className="text-base font-semibold text-white mb-1">
              {isLutVariant ? "Preview da LUT" : "Enviar arquivo"}
            </p>
            <p className="text-xs text-white/50">
              Clique ou arraste o arquivo aqui
            </p>
          </div>

          {/* Informações */}
          <div className="mt-2 flex items-center gap-2 text-xs text-white/40">
            <ImageIcon size={14} className="opacity-70" />
            <span>
              Máx. {MAX_FILE_SIZE_KB}KB
              {isLutVariant && " • JPG"}
              {isCoverVariant && " • 614×768px"}
            </span>
          </div>
        </div>
      </Label>
    </>
  );

  return (
    <div className="w-48 space-y-2">
      {/* Container principal */}
      <div
        className={`
          relative flex flex-col rounded-2xl overflow-hidden border transition-all duration-200
          ${error ? "border-red-500/50 shadow-lg shadow-red-500/20" : "border-white/10"}
          ${isDragActive ? "border-blue-400 bg-blue-500/10 shadow-lg shadow-blue-500/20" : "bg-white/[.02]"}
          ${isDisabled ? "opacity-70" : ""}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Preview ou estado vazio */}
        {displayPreview ? (
          <>
            {renderPreview()}

            {/* Botão de remover */}
            {showRemoveButton && !isDisabled && (
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8 rounded-full shadow-xl hover:scale-110 transition-transform"
                onClick={handleRemove}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </>
        ) : (
          renderEmptyState()
        )}

        {/* Rodapé com título */}
        <div className="w-full text-white py-3 px-4 text-sm font-semibold text-left select-none pointer-events-none border-t border-white/10 bg-gradient-to-b from-white/[.04] to-white/[.02]">
          {isLutVariant ? "Preview LUT" : title}
        </div>
      </div>

      {/* Mensagem de erro */}
      {(error || internalError) && (errorMessage || internalError) && (
        <Alert variant="destructive" className="animate-in fade-in slide-in-from-top-1">
          <AlertDescription className="text-xs">
            {errorMessage || internalError}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
